import { filter, merge, tap } from 'rxjs';
import { store } from '@redux/store';
import { onDocumentKeyDown$ } from 'layout/redux/observables';
import uiSlice from '../redux/uiStateSlice';
import modalService from '../services/modalService';
import Overlay from './overlay';

const modal$ = modalService.getHtml();

const modalHtml = {
  modalContainer: document.querySelector('.modal__box'),
  previousHtml: null,
  previousCallback: null,
  escapeSubscription: null,
};

const disconnectedCallbacks = new Map();
const registeredCallbacks = new Map();
const overlay = new Overlay();

const isEscapeKeyPressed = (event) => event.key === 'Escape';

const isTabKeyPressed = (event) => event.key === 'Tab';

/**
 * Retrieves the first and last focusable elements within the modal container.
 *
 * Focusable elements include: links, image maps, inputs, selects, textareas, and buttons
 * (excluding disabled elements).
 *
 * @function getFocusableElements
 * @returns {{ first: (Element|undefined), last: (Element|undefined) }}
 * An object containing the first and last focusable elements in the modal.
 */
const getFocusableElements = () => {
  const focusableTargets = `a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])`;
  const focusableElements = modalHtml.modalContainer.querySelectorAll(focusableTargets);
  return {
    first: focusableElements[0],
    last: focusableElements[focusableElements.length - 1],
  };
};

/**
 * Handles keyboard navigation within a modal using Tab and Shift+Tab keys.
 * Traps focus within the modal by cycling to the last focusable element when Tab is pressed on the first element,
 * and cycling to the first focusable element when Shift+Tab is pressed on the last element.
 *
 * @param {KeyboardEvent} event - The keyboard event triggered by pressing Tab or Shift+Tab.
 * @returns {void}
 */
const handleTabNavigation = (event) => {
  const { first, last } = getFocusableElements();
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first?.focus();
  }
};

const onEscapeCloseModal$ = onDocumentKeyDown$.pipe(
  filter(() => !!modalHtml.modalContainer),
  filter(() => modalHtml.modalContainer.dataset.modalExpanded === 'true'),
  filter(isEscapeKeyPressed),
);

const onTabNavigation$ = onDocumentKeyDown$.pipe(
  filter(() => !!modalHtml.modalContainer),
  filter(() => modalHtml.modalContainer.dataset.modalExpanded === 'true'),
  filter(isTabKeyPressed),
);

const onDisconnected = (callbackName) => {
  const callbacksDisconnected = disconnectedCallbacks.get(callbackName) ?? [];
  callbacksDisconnected.filter((fn) => typeof fn === 'function').forEach((fn) => fn());

  if (disconnectedCallbacks.has(callbackName)) {
    disconnectedCallbacks.delete(callbackName);
    registeredCallbacks.delete(callbackName);
  }
};

const closeModal = () => {
  const modalInformation = store.getState().uiState?.modalInformation;
  const callbackName = modalInformation?.name;

  modalHtml.escapeSubscription?.unsubscribe();
  modalHtml.escapeSubscription = null;

  onDisconnected(callbackName);

  modalHtml.modalContainer.classList.remove('modal--expanded');
  modalHtml.modalContainer.removeAttribute('data-modal-expanded');

  overlay.remove();

  modalHtml.modalContainer.innerHTML = null;
  modalHtml.previousCallback = null;
  modalHtml.previousHtml = null;

  store.dispatch(uiSlice.actions.clearModal());

  const event = new CustomEvent('modalClosed', {
    detail: { modalInformation },
  });

  setTimeout(() => {
    document.body.dispatchEvent(event);
  }, 100);
};

const disconnectCallback = (data) => {
  const { name, fn } = data;
  const existingCallbacks = disconnectedCallbacks.get(name) ?? [];
  disconnectedCallbacks.set(name, [fn, ...existingCallbacks]);
};

const openModal = (html) => {
  const modalInformation = store.getState().uiState?.modalInformation;
  const callbackName = modalInformation?.name;
  const previousCallbackName = modalInformation?.previousName;

  const callbacksRegistered = registeredCallbacks.get(callbackName) ?? [];

  modalHtml.previousCallback = callbackName;
  modalHtml.previousHtml = modalHtml.modalContainer.innerHTML?.trim();

  if (modalHtml.previousHtml) {
    onDisconnected(previousCallbackName);
  }

  modalHtml.modalContainer.innerHTML = html.value;
  modalHtml.modalContainer.classList.add('modal--expanded');
  modalHtml.modalContainer.setAttribute('data-modal-expanded', 'true');

  overlay.create();

  callbacksRegistered.filter((fn) => typeof fn === 'function').forEach((fn) => fn());

  modalHtml.escapeSubscription?.unsubscribe();
  modalHtml.escapeSubscription = onEscapeCloseModal$.subscribe(() => {
    modalService.clearHtml();
  });
};

const registerCallback = (data) => {
  const { name, fn } = data;
  const existingCallbacks = registeredCallbacks.get(name) ?? [];
  registeredCallbacks.set(name, [fn, ...existingCallbacks]);
};

const setPreviousHtml = () => {
  onDisconnected(modalHtml.previousCallback);
  modalHtml.modalContainer.innerHTML = null;
  modalService.setHtml({ payload: modalHtml.previousHtml });
};

const close = modal$.pipe(
  filter((data) => data.action === 'clearHtml'),
  tap(closeModal),
);

const disconnect = modal$.pipe(
  filter((data) => data.action === 'onHtmlRemoved'),
  tap(disconnectCallback),
);

const open = modal$.pipe(
  filter((data) => data.action === 'setHtml'),
  tap(openModal),
);

const register = modal$.pipe(
  filter((data) => data.action === 'onHtmlReady'),
  tap(registerCallback),
);

const previous = modal$.pipe(
  filter((data) => data.action === 'setPreviousHtml'),
  tap(setPreviousHtml),
);

onTabNavigation$.subscribe(handleTabNavigation);

merge(close, disconnect, open, register, previous).subscribe();
